/**
 * Live-DOM overlays for the procedural effects that can't be expressed as a
 * single CSS property (noise / texture / shader / glass surface). These sit ON
 * TOP of the element content, clipped to its shape, inside the CanvasViewport
 * element wrapper.
 *
 * The paint order here — glass tint → shader → texture → noise → glass highlight
 * — is mirrored exactly in export/renderDocumentToCanvas.js. Keep them in step
 * or the PNG/PDF stops matching the editor.
 *
 * Background blur / glass frost is the one effect that has to sit BEHIND the
 * content, so it gets its own layer here (`ElementBackdrop`) rather than riding
 * on the element wrapper: `backdrop-filter` on the wrapper frosts the wrapper's
 * RECTANGLE, which turned every ellipse, star and rounded box into a square pane
 * of frost — while the export clipped it to the shape (clipToElementShape) and
 * the two surfaces disagreed. Layer blur (`filter: blur`) still belongs to the
 * content, applied by the canvas around this.
 */
import {
    resolveBorderRadius, buildBackdropFilter,
    isNoiseOn, isTextureOn, isShaderOn, isGlassOn, hasOverlayEffect,
} from '../panels/propertyControls';
import { noiseTileUrl, textureTileUrl, shaderFieldUrl } from '../effects/effectTextures';
import { isPathShapeElement, shapePathD } from '../schema/shapeLibrary';

/** The two stacked gradients that make up the glass sheen (matched in export). */
export const GLASS_SHEEN = 'linear-gradient(135deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0.08) 38%, rgba(255,255,255,0) 60%)';
export const GLASS_TOP_EDGE = 'linear-gradient(to bottom, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0) 2.5px)';

function elementRadiusCss(element) {
    const styles = element.styles || {};
    if (element.type === 'shape' && element.shape === 'ellipse') return '50%';
    if (element.type === 'shape' && element.shape === 'line') return 2;
    return resolveBorderRadius(styles);
}

/**
 * A path shape's overlays follow its OUTLINE, not its box — a star's grain has to
 * stop at the star. `clip-path: path()` takes absolute px, which is fine here:
 * the path is rebuilt from the element's current size on every render.
 */
function elementClipPath(element) {
    if (!isPathShapeElement(element)) return undefined;
    const d = shapePathD(element, Math.max(1, element.width), Math.max(1, element.height));
    return d ? `path("${d}")` : undefined;
}

const fullLayer = {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
};

/**
 * What the element's own outline is, as CSS — a radius for the box-ish types, a
 * `clip-path` for the shapes CSS has no name for. The same silhouette the export
 * traces in `clipToElementShape`, so anything clipped with this matches the PNG.
 */
export function elementShapeClip(element) {
    const clip = elementClipPath(element);
    return {
        borderRadius: elementRadiusCss(element),
        clipPath: clip,
        WebkitClipPath: clip,
    };
}

/**
 * Background blur / glass frost, clipped to the element's shape and sitting
 * BEHIND its content.
 *
 * Its own layer for two reasons. The shape: a `backdrop-filter` on the element
 * wrapper frosts that wrapper's rectangle, so a blurred ellipse came out as a
 * blurred square. And the stacking: the frost is the backdrop seen THROUGH the
 * element, so it has to be painted under everything the element draws — which is
 * also the order the export paints it in (drawBackdropBlur, before the layer).
 *
 * It must not sit under a filtered ancestor: a `filter` makes its element a
 * backdrop root, and a backdrop-filter inside one samples nothing. That is why
 * the canvas puts Layer blur on the CONTENT layer beside this, not on the wrapper
 * around both.
 */
export function ElementBackdrop({ element }) {
    const backdrop = buildBackdropFilter(element?.styles || {});
    if (!backdrop) return null;
    return (
        <div
            style={{
                ...fullLayer,
                ...elementShapeClip(element),
                backdropFilter: backdrop,
                WebkitBackdropFilter: backdrop,
            }}
        />
    );
}

export default function ElementEffectsOverlay({ element }) {
    const styles = element.styles || {};
    if (!hasOverlayEffect(styles)) return null;

    const shape = elementShapeClip(element);
    const layers = [];

    // Glass surface tint (bottom of the overlay stack — the frosted pane colour)
    if (isGlassOn(styles)) {
        layers.push(
            <div
                key="glass-tint"
                style={{
                    ...fullLayer,
                    background: styles.glassTint || '#ffffff',
                    opacity: Math.max(0, Math.min(100, Number(styles.glassOpacity) ?? 12)) / 100,
                }}
            />
        );
    }

    // Shader — full-element gradient field
    if (isShaderOn(styles)) {
        const url = shaderFieldUrl(styles.shaderType, element.width, element.height, {
            color1: styles.shaderColor1,
            color2: styles.shaderColor2,
            color3: styles.shaderColor3,
            angle: styles.shaderAngle,
        });
        layers.push(
            <div
                key="shader"
                style={{
                    ...fullLayer,
                    backgroundImage: `url(${url})`,
                    backgroundSize: '100% 100%',
                    opacity: Math.max(0, Math.min(100, Number(styles.shaderOpacity) ?? 100)) / 100,
                    mixBlendMode: styles.shaderBlend || 'normal',
                }}
            />
        );
    }

    // Texture — tiling pattern
    if (isTextureOn(styles)) {
        const url = textureTileUrl(styles.textureType, {
            scale: styles.textureScale,
            color: styles.textureColor,
        });
        layers.push(
            <div
                key="texture"
                style={{
                    ...fullLayer,
                    backgroundImage: `url(${url})`,
                    backgroundRepeat: 'repeat',
                    opacity: Math.max(0, Math.min(100, Number(styles.textureOpacity) ?? 40)) / 100,
                    mixBlendMode: styles.textureBlend || 'multiply',
                }}
            />
        );
    }

    // Noise — tiling grain
    if (isNoiseOn(styles)) {
        const url = noiseTileUrl({
            size: styles.noiseSize,
            density: styles.noiseDensity,
            type: styles.noiseType,
        });
        layers.push(
            <div
                key="noise"
                style={{
                    ...fullLayer,
                    backgroundImage: `url(${url})`,
                    backgroundRepeat: 'repeat',
                    opacity: Math.max(0, Math.min(100, Number(styles.noiseAmount) ?? 0)) / 100,
                    mixBlendMode: styles.noiseBlend || 'overlay',
                }}
            />
        );
    }

    // Glass highlight (very top — the light sheen + top edge)
    if (isGlassOn(styles) && styles.glassHighlight !== false) {
        layers.push(
            <div
                key="glass-hi"
                style={{
                    ...fullLayer,
                    backgroundImage: `${GLASS_TOP_EDGE}, ${GLASS_SHEEN}`,
                }}
            />
        );
    }

    return (
        <div
            style={{
                ...fullLayer,
                ...shape,
                overflow: 'hidden',
            }}
        >
            {layers}
        </div>
    );
}
